import React from "react";
import { useSearchParams } from "react-router-dom";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import OemLookup from "./OemLookup";
import VinLookup from "./VinLookup";
import { useDealerContext } from "../DealerContext.jsx";
import { TEST_DEALERS } from "../../../lib/testDealers";

function DealerSearch({ username, onSearchSubmit, isNickLargent }) {
  const context = useDealerContext();
  const [searchTermInput, setSearchTermInput] = React.useState("");
  const [searchParams] = useSearchParams();
  const [recentDealers, setRecentDealers] = React.useState(() => {
    // getting stored value
    const recents = localStorage.getItem("recents");
    const value = JSON.parse(recents);
    return value || [];
  });

  // Function to add a dealer to the recent list
  const addDealerToRecents = (dealer, searchTerm) => {
    if (!dealer || !dealer.name) return;
    // Always use the original search term as the ID if provided
    const dealerId = searchTerm || dealer.id;
    if (!dealerId) return;
    setRecentDealers(prevRecents => {
      // Remove existing entry if present (to avoid duplicates)
      const filteredRecents = prevRecents.filter(d => d.id !== dealerId);
      // Create dealer object with original search term as id
      const dealerToAdd = {
        ...dealer,
        id: dealerId
      };
      // Add the new dealer to the beginning of the list
      const newRecents = [dealerToAdd, ...filteredRecents];
      // Limit to 10 recent dealers
      const limitedRecents = newRecents.slice(0, 10);
      // Update localStorage
      localStorage.setItem("recents", JSON.stringify(limitedRecents));
      return limitedRecents;
    });
  };

  // Get combined list of dealers for the dropdown
  const displayedDealers = React.useMemo(() => {
    return recentDealers;
  }, [recentDealers]);

  // Get test dealers if user is Nick-Largent
  const testDealersForDisplay = React.useMemo(() => {
    return isNickLargent ? TEST_DEALERS : [];
  }, [isNickLargent]);

  React.useEffect(() => {
    // storing input name
    localStorage.setItem("recents", JSON.stringify(recentDealers));
  }, [recentDealers]);

  const searchTerm = searchParams.get("dealerId");

  const setSearchTerm = (search) => {
    if (search === searchTerm)
      return;
    context.setDealer(search);
  }

  const handleLoad = (e) => {
    if (e) e.preventDefault();
    const term = searchTermInput.trim();
    setSearchTerm(term);
    // Pass both the addDealerToRecents function and the original search term
    if (onSearchSubmit) {
      onSearchSubmit(addDealerToRecents, term);
    }
  }

  return (
    <div className="dealer-search-container">
      <Form.Group className="mb-0 me-2">
        <div className="d-flex align-items-center">
          <input
            id="dealer-search-term-input"
            type="text"
            placeholder="LogicalId, PlatformId, CorgId, SVOC"
            size="28"
            value={searchTermInput}
            onKeyDown={(e) => { if (e.key === "Enter") handleLoad(); }}
            onChange={(e) => setSearchTermInput(e.target.value)}
            className="me-2"
          />
          <Button onClick={handleLoad} className="me-2" size="sm" id="dealer-search-load-button">Load</Button>

          {(recentDealers.length > 0 || testDealersForDisplay.length > 0) && (
            <Dropdown className="d-inline-block me-2">
              <Dropdown.Toggle
                variant="outline-light"
                id="recent-dealers-dropdown"
                size="sm"
              >Recents
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {/* Regular Recent Dealers */}
                {displayedDealers.map((dealer) => (
                  <Dropdown.Item
                    key={dealer.id}
                    onClick={() => {
                      const term = dealer.id.trim();
                      setSearchTerm(term);
                      // Pass both the addDealerToRecents function and the original search term
                      if (onSearchSubmit) {
                        onSearchSubmit(addDealerToRecents, term);
                      }
                    }}
                    title={dealer.id}
                  >
                    {dealer.name}
                  </Dropdown.Item>
                ))}
                
                {/* Test Dealers Section - Only for Nick-Largent */}
                {testDealersForDisplay.length > 0 && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Header>Test Dealers</Dropdown.Header>
                    {testDealersForDisplay.map((dealer) => (
                      <Dropdown.Item
                        key={`test-${dealer.id}`}
                        onClick={() => {
                          const term = dealer.id.trim();
                          setSearchTerm(term);
                          // Pass both the addDealerToRecents function and the original search term
                          if (onSearchSubmit) {
                            onSearchSubmit(addDealerToRecents, term);
                          }
                        }}
                        title={dealer.description}
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        {dealer.name}
                      </Dropdown.Item>
                    ))}
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}
          <div className="d-inline-flex">
            <OemLookup />
            <VinLookup />
          </div>
        </div>
      </Form.Group>
    </div>
  );
}

export default DealerSearch;
